import { UIChart } from '../chart/ui-chart';
import { Component } from '../../../shared-components/decorators/component.decorator';
import { UISeries } from '../series/ui-series';
import { Children } from '../../../shared-components/decorators/child.decorator';
import { PFMMappings } from '../../plugins/vendor/pfm-mapping';
import {
    debounce,
    runInNextFrame,
} from '../../../shared-components/global/helpers';
import type { Point, Options, Chart } from 'highcharts';
import {
    ExtendedPlotSeriesOptions,
    ExtendedPoint,
    PFMCategory,
    PFMCategoryMap,
    UIChartSeriesData,
} from '../charts.types';
import styles from './ui-chart-sausage.css';

/**
 * @class
 * @memberof ChartsComponents
 * @element ui-chart-sausage
 * @augments {UIChart}
 * @alias UIChartSausage
 * @classdesc Represents a class for <code>ui-chart-bar</code> element.
 * Draws bar type chart.
 * @property {ArrayLike<UISeries>} uiSeries shortcut to ui-series children.
 * @slot {@type "ui-chart-sausage"}
 * @example
 * <ui-chart-sausage preset="interactive">
 *   <ui-series data="[77]" name="transport"></ui-series>
 *   <ui-series data="[10]" name="children"></ui-series>
 * </ui-chart-sausage>
 */
@Component({ tag: 'ui-chart-sausage', styles })
export class UIChartSausage extends UIChart {
    @Children('ui-series') uiSeries!: NodeListOf<UISeries>;

    private _sum: number = 0;
    private initialSeries: Array<UISeries> | undefined;
    private windowResizeHandler!: () => void;

    override get type(): string {
        return 'bar';
    }

    get gap(): number {
        return UIChart.BaseGrid / 4;
    }

    get sum(): number {
        return this._sum;
    }

    set sum(value: number) {
        this._sum = value;
    }

    private static _categories?: PFMCategoryMap;

    static get Categories(): PFMCategoryMap {
        return UIChartSausage._categories ?? PFMMappings;
    }

    /**
     * Can be set externally before render, PFM service compatible.
     * @param {UIChartColumn} values
     */
    static setCategories(values: PFMCategoryMap): void {
        UIChartSausage._categories = values;
    }

    static readonly ResizeDebounceTIme = 100;

    static getCategoryByName(name: string | null): PFMCategory | undefined {
        if (!name) {
            return undefined;
        }
        return Object.values(UIChartSausage.Categories).find(
            (category) => category.name === name
        );
    }

    private static roundLabel(x: number): string {
        const pct = Math.round(x * 10) / 10;
        const hasDecimals = pct - Math.floor(pct) !== 0;
        return pct.toFixed(hasDecimals ? 1 : 0);
    }

    private get options(): Options {
        const radius = UIChart.BaseGrid / 2;
        const sum = this._sum;
        const seriesOptions: ExtendedPlotSeriesOptions = {
            borderRadiusTopRight: radius,
            borderRadiusTopLeft: radius,
            borderRadiusBottomRight: radius,
            borderRadiusBottomLeft: radius,
            pointWidth: UIChart.BaseGrid * 3,
            stacking: 'percent',
            dataLabels: {
                align: 'center',
                verticalAlign: 'middle',
                allowOverlap: true,
                enabled: true,
                overflow: 'allow',
                useHTML: true,
                formatter(this: Point): string {
                    const originalPercentage = ((this.y ?? 0) / sum) * 100;
                    const pct = UIChartSausage.roundLabel(originalPercentage);
                    const glyph =
                        UIChartSausage.getCategoryByName(this.series.name)
                            ?.icon ?? 'redesign-question-mark';
                    return `
                <div class="ui-chart-sausage__label">
                    <ui-icon color="white">${glyph}</ui-icon>
                    <span>${pct}%</span>
                </div>`;
                },
            },
        };
        return {
            chart: {
                height: UIChart.BaseGrid * 10,
                margin: 0,
                spacing: [0],
                reflow: false,
                events: {
                    load: this.loadChartEventHandler.bind(this),
                    render: this.renderChartEventHandler.bind(this),
                },
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                series: seriesOptions,
            },
            yAxis: {
                reversed: true,
            },
        };
    }

    private loadChartEventHandler(): void {
        runInNextFrame(() => this.redraw(false, false));
    }

    private renderChartEventHandler(event: Event): void {
        const chart = event.target as unknown as Chart;
        const width = chart.chartWidth;
        if (width === 0) {
            console.warn('sausage chart width is zero');
            return;
        }
        // 2px is min width by design + 2 gaps = 2 + 2 + 2, so these look better
        const minWidth = this.gap * 3;
        const gapDelta = minWidth / width;
        for (let i = chart.series.length - 1; i >= 0; --i) {
            const point: ExtendedPoint = chart.series[i].points[0];
            const pointDataLabel = point.dataLabel;
            const dataLabel = pointDataLabel?.div?.querySelector<HTMLElement>(
                '.ui-chart-sausage__label'
            );
            if (!dataLabel) {
                continue;
            }
            const label = dataLabel.querySelector<HTMLElement>('span');
            const icon = dataLabel.querySelector<HTMLElement>('ui-icon');

            // NB! In bar stack charts dimensions are inverted!
            // dlBox comes from roundedcorners plugin to keep dimensions of the original rectangle.
            const dlBox = point.dlBox;
            const pw = dlBox?.height ?? 0;
            const lw = label?.offsetWidth ?? 0;

            // Hide if icon doesn't fit
            if (label && pw < lw) {
                label.textContent =
                    label.textContent?.replace(/[^0-9,.]/, '') ?? '';
            }

            if ((point.percentage ?? 0) < gapDelta * 100) {
                point.update({ y: gapDelta * this.sum }, false, false);
            }

            // make even better alignment
            if (dataLabel && pw < lw && pw >= UIChart.BaseGrid * 3) {
                dataLabel.style.minWidth = `${pw + this.gap}px`;
            }

            const isLabelHidden =
                lw > pw || Math.round(point.percentage ?? 0) === 0;
            const isIconHidden = pw < UIChart.BaseGrid * 3;
            if (isLabelHidden) {
                label?.classList.add('-visibility-hidden');
            }
            if (isIconHidden) {
                icon?.classList.add('-visibility-hidden');
            }
        }
    }

    private handleWindowResize(): void {
        this.renderChart();
    }

    /**
     * Processes the series.
     * Not very efficient, but this insures a stable sorting and resizing.
     * @param {boolean} [redraw]
     * @private
     */
    private processUISeries(redraw: boolean = false): void {
        this.initialSeries = this.initialSeries ?? [...this.uiSeries];
        try {
            const getValue = (
                item: UIChartSeriesData | number | undefined
            ): number => {
                if (typeof item === 'number') {
                    return item;
                }
                return item?.y ?? 0;
            };
            this.initialSeries.sort((a, b) => {
                const bData = JSON.parse(b.data ?? '[]') as Array<
                    UIChartSeriesData | number
                >;
                const aData = JSON.parse(a.data ?? '[]') as Array<
                    UIChartSeriesData | number
                >;
                return getValue(bData[0]) - getValue(aData[0]);
            });
        } catch (e) {
            console.error(
                `${this.constructor.name}: invalid series data: ${String(e)}`
            );
            return;
        }
        this.append(...this.initialSeries);
        try {
            this.sum = Array.from(this.uiSeries).reduce((prev, next) => {
                const data = JSON.parse(next.data ?? '[]') as Array<number>;
                return prev + (data[0] ?? 0);
            }, 0);
        } catch (e) {
            console.error(`${this.constructor.name}: wrong data: ${String(e)}`);
            return;
        }
        if (redraw) {
            this.redraw();
        }
    }

    private setSeriesColors(): void {
        this.initialSeries?.forEach((series) => {
            const name = series.getAttribute('name');
            const category = UIChartSausage.getCategoryByName(name);
            if (category) {
                series.style.setProperty(
                    '--ui-chart-sausage-series-color',
                    `var(${category.color})` || ''
                );
            }
        });
    }

    /**
     * @inheritDoc
     */
    override renderChart(): void {
        this.processUISeries();
        this.setSeriesColors();
        this.draw(this.options);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        super.hydrate();
        this.windowResizeHandler = debounce(
            this.handleWindowResize.bind(this),
            UIChartSausage.ResizeDebounceTIme
        );
        window.addEventListener('resize', this.windowResizeHandler);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        window.removeEventListener('resize', this.windowResizeHandler);
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        window.addEventListener('resize', this.windowResizeHandler);
    }
}
